import { PhoneFrame } from "@/components/mentor-profile/phone-frame";
import { WizardBackground } from "@/components/mentor-profile/wizard-background";
import { Wordmark } from "@/components/brand/wordmark";
import type { DraftSaveState } from "@/lib/hooks/mentor-profile/use-mentor-profile-draft";
import { cn } from "@/lib/utils";
import { Button, IconButton } from "@somethings-ui/web";
import { ArrowLeft, Loader2 } from "lucide-react";
import { ReactNode } from "react";

export const WIZARD_STEP_COUNT = 8;

interface WizardStepLayoutProps {
  stepIndex: number;
  icon: ReactNode;
  heading: string;
  subheading?: string;
  children: ReactNode;
  preview: ReactNode;
  saveState: DraftSaveState;
  onContinue: () => void;
  continueDisabled?: boolean;
  continueLabel?: string;
  onBack?: () => void;
  reviewerNotes?: string[];
  embedded?: boolean;
}

export function WizardStepLayout({
  stepIndex,
  icon,
  heading,
  subheading,
  children,
  preview,
  saveState,
  onContinue,
  continueDisabled = false,
  continueLabel = "Continue",
  onBack,
  reviewerNotes,
  embedded = false,
}: WizardStepLayoutProps) {
  const progress = Math.round((stepIndex / WIZARD_STEP_COUNT) * 100);

  const notes = reviewerNotes && reviewerNotes.length > 0 ? (
    <div className="mt-5 rounded-lg border border-brand-tangelo/30 bg-[#fff8f4] px-4 py-3">
      <p className="font-body text-xs font-semibold uppercase tracking-[0.3px] text-brand-tangelo">
        From the review team
      </p>
      <ul className="mt-2 space-y-1.5">
        {reviewerNotes.map((note) => (
          <li
            key={note}
            className="font-body text-sm leading-relaxed text-text-primary"
          >
            {note}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  if (embedded) {
    return (
      <div className="flex flex-col gap-3">
        {notes}
        <div className="flex flex-col gap-2">{children}</div>
        <div className="flex h-4 items-center gap-2 text-xs text-text-secondary">
          {saveState === "saving" ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              Saving…
            </>
          ) : saveState === "saved" ? (
            <span>Saved</span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <WizardBackground className="items-start justify-center gap-12">
      <div className="relative z-10 flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-surfaces-z5 bg-surfaces-z0 shadow-[0px_8px_48px_0px_rgba(0,0,0,0.15)]">
        <header className="border-b border-surfaces-z5 px-8 pb-6 pt-4">
          <div className="flex items-end justify-between pb-4">
            <Wordmark className="h-[18px] text-black" />
            <span className="rounded-full bg-surfaces-z3 px-2.5 py-0.5 text-xs font-medium text-text-primary">
              Draft
            </span>
          </div>
          <div className="flex items-center justify-between text-text-secondary">
            <span className="text-sm">Account</span>
            <span className="text-xs">
              Step {stepIndex} of {WIZARD_STEP_COUNT}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-surfaces-z3">
            <div
              className="h-full rounded-full bg-brand-periwinkle transition-[width]"
              style={{ width: `${progress}%` }}
            />
          </div>
        </header>

        <div className="flex flex-col px-8 py-10">
          <div className="flex size-14 items-center justify-center rounded-2xl bg-brand-ecru/60 text-text-primary">
            {icon}
          </div>

          <h1 className="mt-4 font-heading text-[27px] font-bold leading-[1.12] tracking-[-0.675px] text-text-primary">
            {heading}
          </h1>
          {subheading && (
            <p className="mt-2 font-body text-[15px] leading-relaxed text-text-secondary">
              {subheading}
            </p>
          )}

          {notes}

          <div className="mt-7 flex flex-col gap-2">{children}</div>

          <div className="flex h-5 items-center gap-2 pt-3 text-xs text-text-secondary">
            {saveState === "saving" ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                Saving...
              </>
            ) : saveState === "saved" ? (
              <span>All changes saved</span>
            ) : null}
          </div>

          <div className="flex items-center justify-between pt-10">
            <IconButton
              size="md"
              variant="secondary"
              aria-label="Go back"
              className={cn(!onBack && "invisible")}
              disabled={!onBack}
              icon={<ArrowLeft className="size-5" />}
              onClick={onBack}
            />
            <Button
              size="sm"
              fullWidth={false}
              onPress={onContinue}
              disabled={continueDisabled}
            >
              {continueLabel}
            </Button>
          </div>
        </div>
      </div>

      <div className="relative z-10 flex shrink-0 flex-col items-center gap-3 self-start">
        <span className="text-xs text-text-secondary">
          Live preview · what teens see
        </span>
        <PhoneFrame>{preview}</PhoneFrame>
      </div>
    </WizardBackground>
  );
}
